import { noop } from 'lodash';

type RejectablePromise<T> = {
  id: string;
  promise: () => Promise<T>;
  reject: (reason?: unknown) => void;
};

const PROMISE_CANCELLED = 'Promise was cancelled';

/**
 * This class runs promises in LIFO queue
 */
export class PromiseQueue<T> {
  private queue: RejectablePromise<T>[];
  private running: boolean;

  constructor() {
    this.queue = [];
    this.running = false;
  }

  run = (id: string, promise: () => Promise<T>): Promise<T> => {
    const existingPromise = this.queue.find((p) => p.id === id);

    if (existingPromise) {
      return existingPromise.promise();
    }

    return new Promise((resolve, reject) => {
      const task = () =>
        promise()
          .then((result) => {
            resolve(result);
            return result;
          })
          .catch((error) => {
            reject(error);
            throw error;
          });

      this.queue.push({
        id,
        promise: task,
        reject,
      });

      this.runNext();
    });
  };

  cancelAll = () => {
    const queue = [...this.queue];
    this.queue = [];

    for (const promise of queue) {
      promise.reject(PROMISE_CANCELLED);
    }
    this.running = false;
  };

  private runNext = () => {
    if (this.running || this.queue.length === 0) {
      return;
    }

    this.running = true;
    const nextTask = this.queue.shift();

    if (nextTask) {
      nextTask
        .promise()
        .finally(() => {
          this.running = false;
          this.runNext();
        })
        // The rejection was already delivered to the caller via reject() in run(); without this
        // catch it also surfaces as an unhandled promise rejection here.
        .catch(noop);
    }
  };
}
